import React, { useState } from 'react';
import { User, Mail, Phone, Building2, Calendar, Award, Plus, Edit, Trash2, Search, X, Save, MapPin, IdCard } from 'lucide-react';

const PharmacistManagement = () => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedPharmacist, setSelectedPharmacist] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    specialization: '',
    licenseNumber: '',
    pharmacy: '',
    dateOfBirth: '',
    hireDate: '',
    status: 'active',
    address: '',
    emergencyContact: '',
    emergencyPhone: ''
  });

  const pharmacists = [
    {
      id: 1,
      firstName: 'Fatou',
      lastName: 'Ndiaye',
      email: 'f.ndiaye@pharmacie.sn',
      phone: '+221 77 123 45 67',
      specialization: 'Pharmacie Clinique',
      licenseNumber: 'SN-PHR-2015-001234',
      pharmacy: 'Pharmacie Dakar Centre',
      dateOfBirth: '1985-03-15',
      hireDate: '2015-06-01',
      status: 'active',
      address: 'Mermoz, Dakar',
      emergencyContact: 'Mamadou Ndiaye',
      emergencyPhone: '+221 77 987 65 43'
    },
    {
      id: 2,
      firstName: 'Amadou',
      lastName: 'Diop',
      email: 'a.diop@pharmacie.sn',
      phone: '+221 77 234 56 78',
      specialization: 'Pharmacie Hospitalière',
      licenseNumber: 'SN-PHR-2018-002345',
      pharmacy: 'Pharmacie Thies Nord',
      dateOfBirth: '1990-07-22',
      hireDate: '2018-09-15',
      status: 'active',
      address: 'Centre Ville, Thies',
      emergencyContact: 'Aïssatou Diop',
      emergencyPhone: '+221 77 876 54 32'
    },
    {
      id: 3,
      firstName: 'Aïssa',
      lastName: 'Fall',
      email: 'a.fall@pharmacie.sn',
      phone: '+221 77 345 67 89',
      specialization: 'Pharmacie Communautaire',
      licenseNumber: 'SN-PHR-2016-003456',
      pharmacy: 'Pharmacie Mbour',
      dateOfBirth: '1988-11-10',
      hireDate: '2016-03-20',
      status: 'active',
      address: 'Saly, Mbour',
      emergencyContact: 'Omar Fall',
      emergencyPhone: '+221 77 765 43 21'
    },
    {
      id: 4,
      firstName: 'Moussa',
      lastName: 'Sarr',
      email: 'm.sarr@pharmacie.sn',
      phone: '+221 77 456 78 90',
      specialization: 'Pharmacologie',
      licenseNumber: 'SN-PHR-2019-004567',
      pharmacy: 'Pharmacie Saint-Louis',
      dateOfBirth: '1992-05-18',
      hireDate: '2019-01-10',
      status: 'active',
      address: 'Sor, Saint-Louis',
      emergencyContact: 'Marième Sarr',
      emergencyPhone: '+221 77 654 32 10'
    },
    {
      id: 5,
      firstName: 'Khady',
      lastName: 'Ba',
      email: 'k.ba@pharmacie.sn',
      phone: '+221 77 567 89 01',
      specialization: 'Pharmacie Industrielle',
      licenseNumber: 'SN-PHR-2017-005678',
      pharmacy: 'Pharmacie Rufisque',
      dateOfBirth: '1987-09-25',
      hireDate: '2017-11-05',
      status: 'on_leave',
      address: 'Bargny, Rufisque',
      emergencyContact: 'Ibrahima Ba',
      emergencyPhone: '+221 77 543 21 09'
    }
  ];

  const pharmacyList = [
    'Pharmacie Dakar Centre',
    'Pharmacie Thies Nord',
    'Pharmacie Mbour',
    'Pharmacie Saint-Louis',
    'Pharmacie Rufisque',
    'Pharmacie Kaolack',
    'Pharmacie Ziguinchor',
    'Pharmacie Louga',
    'Pharmacie Diourbel'
  ];

  const specializations = [
    'Pharmacie Clinique',
    'Pharmacie Hospitalière',
    'Pharmacie Communautaire',
    'Pharmacologie',
    'Pharmacie Industrielle',
    'Pharmacie Galénique',
    'Toxicologie'
  ];

  const statusOptions = [
    { value: 'all', label: 'Tous' },
    { value: 'active', label: 'Actif' },
    { value: 'on_leave', label: 'En congé' },
    { value: 'inactive', label: 'Inactif' }
  ];

  const filteredPharmacists = pharmacists.filter(pharmacist => {
    const matchesSearch = 
      pharmacist.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pharmacist.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pharmacist.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pharmacist.pharmacy.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || pharmacist.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const badges = {
      active: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Actif', border: 'border-emerald-200' },
      on_leave: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'En congé', border: 'border-orange-200' },
      inactive: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Inactif', border: 'border-gray-200' }
    };
    return badges[status] || badges.active;
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const openAddModal = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      specialization: '',
      licenseNumber: '',
      pharmacy: '',
      dateOfBirth: '',
      hireDate: '',
      status: 'active',
      address: '',
      emergencyContact: '',
      emergencyPhone: ''
    });
    setShowAddModal(true);
  };

  const openEditModal = (pharmacist) => {
    setSelectedPharmacist(pharmacist);
    setFormData({
      firstName: pharmacist.firstName,
      lastName: pharmacist.lastName,
      email: pharmacist.email,
      phone: pharmacist.phone,
      specialization: pharmacist.specialization,
      licenseNumber: pharmacist.licenseNumber,
      pharmacy: pharmacist.pharmacy,
      dateOfBirth: pharmacist.dateOfBirth,
      hireDate: pharmacist.hireDate,
      status: pharmacist.status,
      address: pharmacist.address,
      emergencyContact: pharmacist.emergencyContact,
      emergencyPhone: pharmacist.emergencyPhone
    });
  };

  const closeModal = () => {
    setShowAddModal(false);
    setSelectedPharmacist(null);
  };

  const handleSubmit = () => {
    alert('Fonctionnalité pas encore disponible');
    closeModal();
  };

  const handleDelete = (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce pharmacien ?')) {
      alert('Fonctionnalité pas encore disponible');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="fixed inset-0 overflow-hidden pointer-events-none opacity-30">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, rgb(209, 213, 219) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <main className="relative z-10 p-4 md:p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-black text-gray-800 mb-4">
            Gestion des Pharmaciens
          </h1>
          <p className="text-gray-600 text-lg">Gérez tous les pharmaciens du réseau</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total</p>
                <p className="text-3xl font-bold text-gray-800">{pharmacists.length}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-md">
                <User className="text-white" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Actifs</p>
                <p className="text-3xl font-bold text-emerald-600">
                  {pharmacists.filter(p => p.status === 'active').length}
                </p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-md">
                <User className="text-white" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">En congé</p>
                <p className="text-3xl font-bold text-orange-600">
                  {pharmacists.filter(p => p.status === 'on_leave').length}
                </p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-md">
                <Calendar className="text-white" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Spécialités</p>
                <p className="text-3xl font-bold text-gray-800">{specializations.length}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-md">
                <Award className="text-white" size={24} />
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-gray-200">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="flex-1 w-full md:w-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Rechercher un pharmacien..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex gap-3 w-full md:w-auto">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>

              <button
                onClick={openAddModal}
                className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:shadow-xl transition-all font-bold whitespace-nowrap"
              >
                <Plus size={20} />
                <span>Ajouter</span>
              </button>
            </div>
          </div>
        </div>

        {/* Pharmacists Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPharmacists.map((pharmacist) => {
            const badge = getStatusBadge(pharmacist.status);
            return (
              <div key={pharmacist.id} className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all border border-gray-200 overflow-hidden group">
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                      <User className="text-white" size={32} />
                    </div>
                    <span className={`px-3 py-1.5 ${badge.bg} ${badge.text} rounded-full text-xs font-bold border ${badge.border}`}>
                      {badge.label}
                    </span>
                  </div>
                  <h3 className="font-black text-white text-xl mb-1">
                    Dr. {pharmacist.firstName} {pharmacist.lastName}
                  </h3>
                  <p className="text-white/80 text-sm">{pharmacist.specialization}</p>
                </div>

                <div className="p-6 space-y-3">
                  <div className="flex items-center space-x-3">
                    <Building2 size={18} className="text-gray-400 flex-shrink-0" />
                    <p className="text-gray-700 text-sm font-medium">{pharmacist.pharmacy}</p>
                  </div>

                  <div className="flex items-center space-x-3">
                    <Mail size={18} className="text-gray-400 flex-shrink-0" />
                    <a href={`mailto:${pharmacist.email}`} className="text-gray-700 text-sm hover:text-blue-600 transition-colors truncate">
                      {pharmacist.email}
                    </a>
                  </div>

                  <div className="flex items-center space-x-3">
                    <Phone size={18} className="text-gray-400 flex-shrink-0" />
                    <a href={`tel:${pharmacist.phone}`} className="text-gray-700 text-sm hover:text-blue-600 transition-colors">
                      {pharmacist.phone}
                    </a>
                  </div>

                  <div className="flex items-center space-x-3">
                    <IdCard size={18} className="text-gray-400 flex-shrink-0" />
                    <p className="text-gray-700 text-sm font-mono">{pharmacist.licenseNumber}</p>
                  </div>

                  <div className="flex items-center space-x-3">
                    <Calendar size={18} className="text-gray-400 flex-shrink-0" />
                    <p className="text-gray-700 text-sm">
                      Embauché le {new Date(pharmacist.hireDate).toLocaleDateString('fr-FR')}
                    </p>
                  </div>

                  <div className="pt-3 border-t border-gray-200 flex space-x-2">
                    <button
                      onClick={() => openEditModal(pharmacist)}
                      className="flex-1 px-4 py-2 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-100 transition-all font-semibold text-sm flex items-center justify-center"
                    >
                      <Edit size={16} className="mr-2" />
                      Modifier
                    </button>
                    <button
                      onClick={() => handleDelete(pharmacist.id)}
                      className="px-4 py-2 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition-all"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {filteredPharmacists.length === 0 && (
          <div className="text-center py-12">
            <User size={64} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 text-lg">Aucun pharmacien trouvé</p>
          </div>
        )}
      </main>

      {/* Add/Edit Modal */}
      {(showAddModal || selectedPharmacist) && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-gray-200">
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex items-center justify-between z-10 rounded-t-3xl">
              <h2 className="text-2xl font-black text-white">
                {selectedPharmacist ? '✏️ Modifier le pharmacien' : '➕ Nouveau pharmacien'}
              </h2>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-white/20 rounded-xl transition-all text-white"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                {/* Personal Information */}
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <h3 className="font-bold text-gray-800 mb-4 flex items-center">
                    <User size={20} className="mr-2" />
                    Informations personnelles
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Prénom *
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Prénom"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Nom *
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Nom"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Date de naissance *
                      </label>
                      <input
                        type="date"
                        name="dateOfBirth"
                        value={formData.dateOfBirth}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Adresse
                      </label>
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Adresse"
                      />
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <h3 className="font-bold text-gray-800 mb-4 flex items-center">
                    <Phone size={20} className="mr-2" />
                    Contact
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="email@pharmacie.sn"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Téléphone *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="+221 77 123 45 67"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Contact d'urgence
                      </label>
                      <input
                        type="text"
                        name="emergencyContact"
                        value={formData.emergencyContact}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Nom du contact"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Téléphone d'urgence
                      </label>
                      <input
                        type="tel"
                        name="emergencyPhone"
                        value={formData.emergencyPhone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="+221 77 123 45 67"
                      />
                    </div>
                  </div>
                </div>

                {/* Professional Information */}
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <h3 className="font-bold text-gray-800 mb-4 flex items-center">
                    <Award size={20} className="mr-2" />
                    Informations professionnelles
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Numéro de licence *
                      </label>
                      <input
                        type="text"
                        name="licenseNumber"
                        value={formData.licenseNumber}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="SN-PHR-2024-XXXXXX"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Spécialisation *
                      </label>
                      <select
                        name="specialization"
                        value={formData.specialization}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Sélectionner...</option>
                        {specializations.map(spec => (
                          <option key={spec} value={spec}>{spec}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Pharmacie affectée *
                      </label>
                      <select
                        name="pharmacy"
                        value={formData.pharmacy}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Sélectionner...</option>
                        {pharmacyList.map(pharmacy => (
                          <option key={pharmacy} value={pharmacy}>{pharmacy}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Date d'embauche *
                      </label>
                      <input
                        type="date"
                        name="hireDate"
                        value={formData.hireDate}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">
                        Statut *
                      </label>
                      <select
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="active">Actif</option>
                        <option value="on_leave">En congé</option>
                        <option value="inactive">Inactif</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex space-x-3 pt-6 border-t border-gray-200 mt-6">
                <button
                  onClick={closeModal}
                  className="flex-1 px-6 py-3 bg-gray-100 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-200 transition-all font-bold"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSubmit}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:shadow-xl transition-all font-bold"
                >
                  <Save size={20} className="inline mr-2" />
                  {selectedPharmacist ? 'Mettre à jour' : 'Enregistrer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PharmacistManagement;